import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { CartItem } from '../types';
import { calculatePrice } from '../utils/pricing';
import { usePricing } from './PricingContext';

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const CART_STORAGE_KEY = 'keystoneprinter_cart';

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { pricingSettings } = usePricing();
  
  // Initialize cart from localStorage
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Validate that it's an array
        if (Array.isArray(parsedCart)) {
          return parsedCart;
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    return [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [items]);

  const addItem = (item: CartItem) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((i) => i.id === item.id);
      if (existingItem) {
        return prevItems.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prevItems, item];
    });
  };

  const removeItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
    } else {
      setItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === id) {
            // Recalculate the price with the new quantity to apply volume discounts
            const quoteRequest = {
              product: item.product,
              width: item.width,
              height: item.height,
              quantity: quantity,
              material: item.material,
              finishing: item.finishing,
              colorSpec: item.colorSpec,
              customOptions: item.customOptions
            };
            
            // Only recalculate if we have the necessary data loaded
            if (item.product.materials && item.product.finishingOptions && pricingSettings.volumeDiscounts) {
              const totalPrice = calculatePrice(
                quoteRequest, 
                item.product.materials, 
                item.product.finishingOptions, 
                pricingSettings.volumeDiscounts
              );
              const unitPrice = totalPrice / quantity;
              return { ...item, quantity, price: unitPrice };
            }
            
            // If data isn't loaded yet, just update the quantity
            return { ...item, quantity };
          }
          return item;
        })
      );
    }
  };

  const clearCart = () => {
    setItems([]);
    // Also clear from localStorage
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};